import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  MapPin, 
  Truck, 
  Package, 
  Camera, 
  FileText,
  Clock,
  User,
  Calendar
} from 'lucide-react';
import { DeliveryProof, DELIVERY_CONDITIONS, DELIVERY_METHODS } from '@/lib/types/delivery';
import { useDeliveryProofs } from '@/hooks/useDeliveryProofs';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface DeliveryReviewInterfaceProps {
  deliveryProof: DeliveryProof;
  onReviewComplete?: () => void;
}

export const DeliveryReviewInterface = ({ deliveryProof, onReviewComplete }: DeliveryReviewInterfaceProps) => {
  const { reviewDeliveryProof } = useDeliveryProofs();
  const { toast } = useToast();
  const [reviewDecision, setReviewDecision] = useState<'accepted' | 'rejected' | 'needs_clarification' | ''>('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getAutoAcceptCountdown = () => {
    const autoAcceptDate = new Date(deliveryProof.auto_accept_at);
    const now = new Date();
    const timeRemaining = autoAcceptDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 0) {
      return { message: 'Auto-accept has occurred', urgent: true };
    } else if (daysRemaining === 1) {
      return { message: 'Auto-accepts tomorrow', urgent: true };
    } else {
      return { message: `Auto-accepts in ${daysRemaining} days`, urgent: daysRemaining <= 2 };
    }
  };

  const countdown = getAutoAcceptCountdown();
  const isReviewed = deliveryProof.client_review_status !== 'pending';
  const deliveryMethodLabel = DELIVERY_METHODS.find(m => m.value === deliveryProof.delivery_method)?.label || deliveryProof.delivery_method;
  const conditionLabel = DELIVERY_CONDITIONS.find(c => c.value === deliveryProof.condition_on_delivery)?.label || deliveryProof.condition_on_delivery;

  const handleSubmitReview = async () => {
    if (!reviewDecision) {
      toast({
        title: "Review Decision Required",
        description: "Please select your review decision",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      await reviewDeliveryProof(deliveryProof.id, {
        status: reviewDecision,
        notes: reviewNotes || undefined,
      });

      toast({
        title: `Delivery ${reviewDecision}`,
        description: reviewDecision === 'accepted' 
          ? "The delivery has been accepted successfully"
          : reviewDecision === 'rejected'
          ? "The vendor will be notified of the rejection"
          : "The vendor will be asked to provide clarification",
      });

      setShowReviewDialog(false);
      onReviewComplete?.();
    } catch (error) {
      toast({
        title: "Review Failed",
        description: error instanceof Error ? error.message : 'Failed to submit review',
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Auto-Accept Warning */}
      {!isReviewed && (
        <Card className={`${countdown.urgent ? 'border-destructive' : 'border-warning'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${countdown.urgent ? 'text-destructive' : 'text-warning'}`} />
              Review Deadline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              <strong>{countdown.message}</strong> - If you don't review by then, 
              the delivery will be automatically accepted.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-accept date: {format(new Date(deliveryProof.auto_accept_at), 'PPP p')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delivery Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Delivery Summary
            </span>
            <Badge variant={
              deliveryProof.client_review_status === 'accepted' ? 'default' :
              deliveryProof.client_review_status === 'rejected' ? 'destructive' :
              deliveryProof.client_review_status === 'needs_clarification' ? 'secondary' :
              'outline'
            }>
              {deliveryProof.client_review_status === 'needs_clarification' ? 'Needs Clarification' : deliveryProof.client_review_status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Delivery Date</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(deliveryProof.delivery_date), 'PPP')}
                  {deliveryProof.delivery_time && ` at ${deliveryProof.delivery_time}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Method</p>
                <p className="text-sm text-muted-foreground">{deliveryMethodLabel}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Condition</p>
                <p className="text-sm text-muted-foreground">{conditionLabel}</p>
              </div>
            </div>
          </div>

          {deliveryProof.partial_delivery && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Partial Delivery</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                This is marked as a partial delivery. Additional deliveries may be expected.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Location */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Delivery Location
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="font-medium">{deliveryProof.delivery_location.delivery_name}</p>
            <div className="text-sm text-muted-foreground">
              <p>{deliveryProof.delivery_location.address_line1}</p>
              {deliveryProof.delivery_location.address_line2 && (
                <p>{deliveryProof.delivery_location.address_line2}</p>
              )}
              <p>
                {deliveryProof.delivery_location.city}, {deliveryProof.delivery_location.state_province} {deliveryProof.delivery_location.postal_code}
              </p>
            </div>

            {deliveryProof.delivery_location.contact_name && (
              <div className="flex items-center gap-2 mt-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {deliveryProof.delivery_location.contact_name}
                  {deliveryProof.delivery_location.contact_phone && ` • ${deliveryProof.delivery_location.contact_phone}`}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Carrier Information */}
      {deliveryProof.carrier_info && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Carrier & Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deliveryProof.carrier_info.carrier_name && (
                <div>
                  <p className="text-sm font-medium">Carrier</p>
                  <p className="text-sm text-muted-foreground">{deliveryProof.carrier_info.carrier_name}</p>
                </div>
              )}

              {deliveryProof.carrier_info.tracking_number && (
                <div>
                  <p className="text-sm font-medium">Tracking Number</p>
                  <p className="text-sm text-muted-foreground font-mono">{deliveryProof.carrier_info.tracking_number}</p>
                </div>
              )}

              {deliveryProof.carrier_info.driver_name && (
                <div>
                  <p className="text-sm font-medium">Driver</p>
                  <p className="text-sm text-muted-foreground">
                    {deliveryProof.carrier_info.driver_name}
                    {deliveryProof.carrier_info.driver_contact && ` • ${deliveryProof.carrier_info.driver_contact}`}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Photos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Delivery Documentation
            <Badge variant="outline">
              {deliveryProof.delivery_photos?.length || 0} photos
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deliveryProof.delivery_photos && deliveryProof.delivery_photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {deliveryProof.delivery_photos.map((photo, index) => (
                <div key={index} className="space-y-2">
                  <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                    <Camera className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="text-xs">
                    <Badge variant="outline" className="text-xs">
                      {photo.photo_type.replace('_', ' ')}
                    </Badge>
                    {photo.caption && (
                      <p className="text-muted-foreground mt-1">{photo.caption}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No photos provided</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Notes */}
      {(deliveryProof.delivery_notes || deliveryProof.exceptions_noted) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Delivery Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {deliveryProof.delivery_notes && (
              <div>
                <Label className="text-sm font-medium">General Notes</Label>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                  {deliveryProof.delivery_notes}
                </p>
              </div>
            )}

            {deliveryProof.exceptions_noted && (
              <>
                {deliveryProof.delivery_notes && <Separator />}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Exceptions or Issues
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                    {deliveryProof.exceptions_noted}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Installation Details */}
      {deliveryProof.delivery_method === 'installation' && (deliveryProof.installation_notes || deliveryProof.warranty_activation || deliveryProof.training_provided) && (
        <Card>
          <CardHeader>
            <CardTitle>Installation Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {deliveryProof.installation_notes && (
              <div>
                <Label className="text-sm font-medium">Installation Notes</Label>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                  {deliveryProof.installation_notes}
                </p>
              </div>
            )}

            <div className="flex gap-4">
              {deliveryProof.warranty_activation && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Warranty Activated
                </Badge>
              )}

              {deliveryProof.training_provided && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Training Provided
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Actions */}
      {!isReviewed && (
        <Card>
          <CardHeader>
            <CardTitle>Review Delivery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setReviewDecision('needs_clarification');
                  setShowReviewDialog(true);
                }}
              >
                Request Clarification
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setReviewDecision('rejected');
                  setShowReviewDialog(true);
                }}
              >
                Reject Delivery
              </Button>
              <Button
                onClick={() => {
                  setReviewDecision('accepted');
                  setShowReviewDialog(true);
                }}
              >
                Accept Delivery
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review History */}
      {isReviewed && (
        <Card>
          <CardHeader>
            <CardTitle>Review History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              {deliveryProof.client_review_status === 'accepted' ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : deliveryProof.client_review_status === 'rejected' ? (
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={
                    deliveryProof.client_review_status === 'accepted' ? 'default' :
                    deliveryProof.client_review_status === 'rejected' ? 'destructive' :
                    'secondary'
                  }>
                    {deliveryProof.client_review_status === 'needs_clarification' ? 'Needs Clarification' : deliveryProof.client_review_status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {deliveryProof.client_reviewed_at && format(new Date(deliveryProof.client_reviewed_at), 'PPP p')}
                  </span>
                </div>
                
                {deliveryProof.client_review_notes && (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {deliveryProof.client_review_notes}
                  </p>
                )}
              </div>
            </div>

            {deliveryProof.client_review_status === 'rejected' && deliveryProof.resubmission_count < deliveryProof.max_resubmissions && (
              <div className="bg-muted/50 p-3 rounded-lg mt-4">
                <p className="text-sm font-medium">Resubmission Available</p>
                <p className="text-sm text-muted-foreground">
                  The vendor can resubmit their delivery proof with corrections.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDecision === 'accepted' ? 'Accept Delivery' :
               reviewDecision === 'rejected' ? 'Reject Delivery' :
               'Request Clarification'}
            </DialogTitle>
            <DialogDescription>
              {reviewDecision === 'accepted' ? 'Confirm that you accept this delivery as satisfactory.' :
               reviewDecision === 'rejected' ? 'Please provide details about why this delivery is being rejected.' :
               'Request additional information or clarification from the vendor.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="review_notes">
                {reviewDecision === 'rejected' ? 'Rejection Reason (Required)' : 
                 reviewDecision === 'needs_clarification' ? 'Clarification Needed (Required)' :
                 'Notes (Optional)'}
              </Label>
              <Textarea
                id="review_notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  reviewDecision === 'rejected' ? "Please explain what needs to be corrected..." :
                  reviewDecision === 'needs_clarification' ? "Please specify what clarification is needed..." :
                  "Any additional feedback or comments..."
                }
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submitting || (reviewDecision !== 'accepted' && !reviewNotes.trim())}
              variant={reviewDecision === 'accepted' ? 'default' : 'destructive'}
            >
              {submitting ? 'Submitting...' : 
               reviewDecision === 'accepted' ? 'Accept Delivery' :
               reviewDecision === 'rejected' ? 'Reject Delivery' :
               'Request Clarification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};